export const ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
};

export const ADMIN_SESSION_STORAGE_KEY = "admin-session-user";

const DEFAULT_SUPER_ADMIN = {
  id: "mock-super-admin",
  fullName: "Super Admin",
  name: "Super Admin",
  email: "superadmin@adhyayi.com",
  phoneNumber: "",
  role: ADMIN_ROLES.SUPER_ADMIN,
  status: "active",
  assignedTenantId: "__all_tenants__",
  assignedTenantIds: ["__all_tenants__"],
};

const hasStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

export const normalizeAdminRole = (role) =>
  role === ADMIN_ROLES.SUPER_ADMIN ? ADMIN_ROLES.SUPER_ADMIN : ADMIN_ROLES.ADMIN;

export const inferRoleFromEmail = (email = "") => {
  const normalizedEmail = String(email).trim().toLowerCase();

  if (!normalizedEmail) {
    return null;
  }

  return normalizedEmail.includes("super")
    ? ADMIN_ROLES.SUPER_ADMIN
    : null;
};

const normalizeTenantIds = (user = {}) => {
  const source =
    user.assignedTenantIds ||
    user.tenantIds ||
    (Array.isArray(user.tenants) ? user.tenants.map((tenant) => tenant?._id || tenant?.id || tenant) : []);

  const normalized = Array.isArray(source)
    ? source.filter(Boolean).map((tenantId) => String(tenantId))
    : [];

  if (isNonEmptyString(user.assignedTenantId) && !normalized.includes(user.assignedTenantId)) {
    normalized.unshift(user.assignedTenantId);
  }

  return normalized;
};

export const normalizeSessionUser = (user = {}) => {
  const assignedTenantIds = normalizeTenantIds(user);
  const fullName =
    user.fullName ||
    user.name ||
    user.userName ||
    user.username ||
    DEFAULT_SUPER_ADMIN.fullName;
  const email = user.email || DEFAULT_SUPER_ADMIN.email;
  const inferredRole = inferRoleFromEmail(email);
  const role = normalizeAdminRole(user.role || user.adminRole || inferredRole || DEFAULT_SUPER_ADMIN.role);
  const assignedTenantId =
    user.assignedTenantId ||
    assignedTenantIds[0] ||
    (role === ADMIN_ROLES.SUPER_ADMIN ? DEFAULT_SUPER_ADMIN.assignedTenantId : "");

  return {
    id: user.id || user._id || DEFAULT_SUPER_ADMIN.id,
    fullName,
    name: fullName,
    email,
    phoneNumber: user.phoneNumber || user.phone || "",
    role,
    status: user.status || "active",
    assignedTenantId,
    assignedTenantIds:
      assignedTenantIds.length > 0
        ? assignedTenantIds
        : assignedTenantId
          ? [assignedTenantId]
          : [],
    profilePicUrl: user.profilePicUrl || "",
  };
};

export const getStoredSessionUser = () => {
  if (!hasStorage()) {
    return null;
  }

  const rawValue = window.localStorage.getItem(ADMIN_SESSION_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  const parsedValue = safeParse(rawValue);

  return parsedValue ? normalizeSessionUser(parsedValue) : null;
};

export const storeSessionUser = (user) => {
  if (!hasStorage()) {
    return null;
  }

  const normalizedUser = normalizeSessionUser(user);
  window.localStorage.setItem(
    ADMIN_SESSION_STORAGE_KEY,
    JSON.stringify(normalizedUser),
  );

  return normalizedUser;
};

export const clearSessionUser = () => {
  if (!hasStorage()) {
    return;
  }

  window.localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
};

export const resolveSessionUser = (apiUser) => {
  const storedUser = getStoredSessionUser();
  const hasToken = hasStorage() && Boolean(window.localStorage.getItem("token"));

  if (!apiUser && !storedUser) {
    return hasToken ? { ...DEFAULT_SUPER_ADMIN } : null;
  }

  const mergedUser = {
    ...storedUser,
    ...apiUser,
    role:
      apiUser?.role ||
      apiUser?.adminRole ||
      storedUser?.role ||
      inferRoleFromEmail(apiUser?.email || storedUser?.email) ||
      DEFAULT_SUPER_ADMIN.role,
  };

  return normalizeSessionUser(mergedUser);
};

export const isSuperAdminUser = (user) =>
  normalizeAdminRole(user?.role) === ADMIN_ROLES.SUPER_ADMIN;

export const canAccessRoles = (user, allowedRoles = []) => {
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
    return true;
  }

  const normalizedRole = normalizeAdminRole(user?.role);

  if (normalizedRole === ADMIN_ROLES.SUPER_ADMIN) {
    return true;
  }

  return allowedRoles.includes(normalizedRole);
};
